import React, { useEffect, useRef, useState } from "react";

/**
 * Animated counter that runs once when scrolled into view.
 * Accepts a numeric value (string or number); strips non-digits to find the target.
 */
export default function CountUp({ value, suffix = "", duration = 1500, className = "" }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState("0");
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const target = parseInt(String(value).replace(/[^\d]/g, ""), 10) || 0;

    if (!ref.current) return;
    if (!("IntersectionObserver" in window)) {
      setDisplay(String(target));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            setStarted(true);
            io.disconnect();
            const start = performance.now();
            const animate = (now) => {
              const elapsed = now - start;
              const progress = Math.min(elapsed / duration, 1);
              const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
              const cur = Math.floor(eased * target);
              setDisplay(String(cur));
              if (progress < 1) requestAnimationFrame(animate);
              else setDisplay(String(target));
            };
            requestAnimationFrame(animate);
          }
        });
      },
      { threshold: 0.3 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [value, duration, started]);

  return (
    <span ref={ref} className={className}>
      {display}
      {suffix}
    </span>
  );
}
